import type { UIMessage } from "@ai-sdk/react";
import { SPEC_DATA_PART_TYPE } from "@json-render/core";
import { type DataPart, useJsonRenderMessage } from "@json-render/react";
import { useMemo } from "react";
import { assistantMessageSuppressesStreamingQuips } from "@/components/herald-chat/heraldToolLoadingLabels";
import { useFeature } from "@/lib/feature-management";
import {
  buildMessageSegments,
  type MessagePart,
} from "@/lib/utils/aiChatUtils";
import useRandomMessageRotation from "./useRandomMessageRotation";

const EMPTY_UI_PARTS: ReadonlyArray<MessagePart> = [];

export interface UseStreamingQuipForLatestAssistantParams {
  messages: UIMessage[];
  lastVisibleAssistantId: string | null;
  isStreaming: boolean;
  showPendingThinking: boolean;
  showDevToolCalls: boolean;
}

export const useStreamingQuipForLatestAssistant = ({
  messages,
  lastVisibleAssistantId,
  isStreaming,
  showPendingThinking,
  showDevToolCalls,
}: UseStreamingQuipForLatestAssistantParams) => {
  const streamingQuipsFeatureOn = useFeature("enable-streaming-quips", false);

  const isLatestAssistantStreaming =
    isStreaming && !showPendingThinking && lastVisibleAssistantId != null;

  const shouldDeriveQuipState =
    streamingQuipsFeatureOn && isLatestAssistantStreaming;

  const latestAssistantParts = useMemo((): ReadonlyArray<MessagePart> => {
    if (lastVisibleAssistantId == null) {
      return EMPTY_UI_PARTS;
    }
    const message = messages.find(
      (m) => m.id === lastVisibleAssistantId && m.role === "assistant",
    );
    if (message == null || !("parts" in message)) {
      return EMPTY_UI_PARTS;
    }
    return message.parts as ReadonlyArray<MessagePart>;
  }, [messages, lastVisibleAssistantId]);

  const partsForQuipRender = (shouldDeriveQuipState
    ? latestAssistantParts
    : EMPTY_UI_PARTS) as unknown as DataPart[];

  const { spec, hasSpec } = useJsonRenderMessage(partsForQuipRender);

  const segments = useMemo(
    () =>
      shouldDeriveQuipState
        ? buildMessageSegments(latestAssistantParts, SPEC_DATA_PART_TYPE)
        : [],
    [shouldDeriveQuipState, latestAssistantParts],
  );

  const suppressStreamingQuips =
    streamingQuipsFeatureOn &&
    assistantMessageSuppressesStreamingQuips({
      segments,
      hasSpec,
      spec,
      isLatestAssistantStreaming,
      showDevToolCalls,
    });

  return useRandomMessageRotation({
    enabled:
      streamingQuipsFeatureOn &&
      isStreaming &&
      !showPendingThinking &&
      !suppressStreamingQuips,
  });
};
